import React, { useMemo } from "react";
import { Flex, LoadingIndicator, Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { useGetChildWorkItemsQuery } from "#graphql/supergraphHooks";
import { SPACING } from "#utils/spacing";
import * as styles from "./styles_vanilla.css";
import { TasksTable } from "./TasksTable";

type TasksTabProps = {
  parentIssueKey: string;
  jiraBaseURL: string;
};

const extractNumericPart = (key: string): number => {
  const parts = key.split("-");
  const numericPart = parts[parts.length - 1];
  return parseInt(numericPart, 10) || 0;
};

export const TasksTab: React.FC<TasksTabProps> = ({ parentIssueKey, jiraBaseURL }) => {
  const { data, loading, error } = useGetChildWorkItemsQuery({
    variables: { parentIssueKey },
    fetchPolicy: "network-only",
  });

  const rawTasks = data?.childWorkItems?.items || [];

  const tasks = useMemo(() => {
    return [...rawTasks].sort((a, b) => extractNumericPart(a.key) - extractNumericPart(b.key));
  }, [rawTasks]);

  return (
    <Flex flexDirection="column" gap={SPACING.MEDIUM} className={styles.tabContentContainer}>
      {loading && (
        <Flex alignItems="center" justifyContent="center" className={styles.loadingContainer}>
          <LoadingIndicator size="medium" data-testid="tasks-loading-indicator" />
        </Flex>
      )}
      {error && (
        <Text variant="regular-md" color={Colors.Danger600}>
          Error loading tasks: {error.message}
        </Text>
      )}
      {!loading && !error && !tasks.length && (
        <Text variant="regular-md" color={Colors.Neutral600}>
          No tasks found
        </Text>
      )}
      {!loading && !error && tasks.length > 0 && (
        <TasksTable tasks={tasks} jiraBaseURL={jiraBaseURL} />
      )}
    </Flex>
  );
};
